import _ from 'lodash';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  fetchDeviceEvents,
  fetchDeviceEventsCountV2,
  getStripIndex,
  getStripPosition,
} from '@/entities/strip';
import {
  EMITTER_CONSTANTS,
  KEY_RECOIL,
  STRIP_DEVICE_THUMBNAIL_INFO,
  DEVICE_EVENT_TAB_ENUM,
  DEVICE_TAB_TYPES,
} from '@/shared/config';
import { useDeepCompareEffect, useEmitter, useUpdateEffect } from '@/shared/lib/customHooks';
import emitter from '@/shared/lib/eventEmitter';
import {
  selectActiveTab,
  selectChangeStripData,
  selectDeviceEventData,
  selectDeviceStripFilter,
  selectIsLoadingStrip,
  selectPageIndex,
  selectReloadStripManagement,
  selectSelectedMultipleStrip,
  selectSelectedStrip,
  useReportStore,
  BEAT_HR_TAB_ID,
} from '@/entities/report';
import { logError } from '@/shared/lib/apiHelpers';
import {
  cacheDeviceEvents,
  formatDeviceEvents,
  getDeviceFilter,
} from '@/widgets/strips-management/ui/handler';
import type { DeviceStripEventsProps } from './deviceStripEvents';

/**
 * Loads and navigates the device event strips for the active sub-tab. Fetches a page of events with their counts, merges pending changes into the list, keeps the store's selected strip valid as the list changes, resolves drag selections into the multiple-strip selection, moves the selection with the arrow keys and pages at the row edges, and refetches on filter, update or reload signals.
 *
 * @param props - Tab props with the store key, study id, report data, active sub-tab and whether the tab is active.
 * @returns An object carrying the selection area ref, the merged event strips, the selected and multi-selected strips, the loading flag, the thumbnail size config and the selection handlers.
 */
const useDeviceStripEvents = ({
  keyRecoil,
  activeButton: activeButtonProp,
  studyId,
  reportData,
  isActive,
}: DeviceStripEventsProps) => {
  const isActiveQuery = useRef(false);

  const activeButton = useReportStore(selectActiveTab(KEY_RECOIL.STRIP_MANAGEMENT));
  const pageIndex = useReportStore(selectPageIndex(keyRecoil));
  const setPageIndexAction = useReportStore((s) => s.actions.setPageIndex);
  /**
   * Writes the strip grid page index for this tab into the report store. Forwards the value to the store action with the tab key already bound.
   *
   * @param v - New page index, or an updater receiving the current one.
   * @returns Nothing.
   */
  const setPageIndex = (v: Parameters<typeof setPageIndexAction>[1]) =>
    setPageIndexAction(keyRecoil, v);
  const sizeThumbnailConfig = useReportStore((s) => s.sizeStripDeviceThumbnailConfig);
  const selectedStrip = useReportStore(selectSelectedStrip(keyRecoil));
  const setSelectedStripAction = useReportStore((s) => s.actions.setSelectedStrip);
  /**
   * Writes the currently selected strip for this tab into the report store. Forwards the value to the store action with the tab key already bound.
   *
   * @param v - New selected strip, or an updater receiving the current one.
   * @returns Nothing.
   */
  const setSelectedStrip = (v: Parameters<typeof setSelectedStripAction>[1]) =>
    setSelectedStripAction(keyRecoil, v);
  const selectedMultipleStrip = useReportStore(selectSelectedMultipleStrip(keyRecoil));
  const setSelectedMultipleStripAction = useReportStore((s) => s.actions.setSelectedMultipleStrip);
  /**
   * Writes the multi-strip selection for this tab into the report store. Forwards the value to the store action with the tab key already bound.
   *
   * @param v - New selected strip list, or an updater receiving the current one.
   * @returns Nothing.
   */
  const setSelectedMultipleStrip = (v: Parameters<typeof setSelectedMultipleStripAction>[1]) =>
    setSelectedMultipleStripAction(keyRecoil, v);
  const deviceEventData: any = useReportStore(selectDeviceEventData(keyRecoil));
  const changeStripData = useReportStore(selectChangeStripData(keyRecoil));
  const stripFilter = useReportStore(selectDeviceStripFilter(keyRecoil));
  const isReloadStripManagement = useReportStore(selectReloadStripManagement(keyRecoil));
  const isLoadingStrip = useReportStore(selectIsLoadingStrip(keyRecoil));
  const setIsLoadingStripAction = useReportStore((s) => s.actions.setIsLoadingStrip);
  /**
   * Writes the strip loading flag for this tab into the report store. Forwards the value to the store action with the tab key already bound.
   *
   * @param v - New loading flag, or an updater receiving the current one.
   * @returns Nothing.
   */
  const setIsLoadingStrip = (v: Parameters<typeof setIsLoadingStripAction>[1]) =>
    setIsLoadingStripAction(keyRecoil, v);
  /**
   * Reads the tab's cached device events straight off the report store. Selects by tab key against the current store snapshot instead of subscribing, so async callers see the latest value.
   *
   * @returns The device event data currently held for this tab.
   */
  const getDeviceEventData = () => selectDeviceEventData(keyRecoil)(useReportStore.getState());
  const setDeviceEventDataAction = useReportStore((s) => s.actions.setDeviceEventData);
  /**
   * Writes the tab's device event data into the report store. Forwards the value to the store action with the tab key already bound.
   *
   * @param v - New device event data, or an updater receiving the current value.
   * @returns Nothing.
   */
  const setDeviceEventData = (v: Parameters<typeof setDeviceEventDataAction>[1]) =>
    setDeviceEventDataAction(keyRecoil, v);
  const setTotalStrip = useReportStore((s) => s.actions.setTotalStripDevice);
  const [deviceEventCount, setDeviceEventCount] = useState<any>({});

  const selectionAreaWrapperRef = useRef<any>();
  const arrowKeyToPrevPage = useRef(false);
  const stripPosition = useRef({ x: 0, y: 0 });
  const [selected, setSelected] = useState<Set<any>>(new Set());

  /**
   * Loads one page of device events for the active sub-tab together with the per-type counts. Skips a non-device tab, picks the query variant from the sub-tab, applies the tab's strip filter to both requests, falls back to the first page when the count no longer reaches the current one, formats the events against the study timezone and facility, caches them and merges them into the store.
   *
   * @returns A promise resolving once the page of events and their counts are in the store.
   */
  const fetchEvents = async () => {
    if (!DEVICE_TAB_TYPES.includes(activeButton as any)) return;
    const { facility, start: startStudyDate } = reportData?.study || {};
    setIsLoadingStrip(true);
    const queryKey =
      activeButtonProp === DEVICE_EVENT_TAB_ENUM.OTHER
        ? 2
        : activeButtonProp === DEVICE_EVENT_TAB_ENUM.PATIENT_TRIGGERED
          ? 1
          : 0;
    try {
      const { timezone } = reportData?.study || {};
      const { id } = reportData?.report || {};
      const validatePage = pageIndex.index <= 0 ? 0 : pageIndex.index;
      const deviceEventFilter = {
        studyId,
        reportId: id,
        limit: STRIP_DEVICE_THUMBNAIL_INFO.stripDisplayLimit,
        order: stripFilter.order || 'asc',
        sortField: 'start',
        page: validatePage + 1,
        selectedField: activeButton,
      };
      const countFilter = {
        studyId,
        reportId: id,
      };
      const { hasAppliedFilter, filter } = getDeviceFilter({
        stripFilter,
        study: reportData?.study,
      });
      if (hasAppliedFilter) {
        _.assign(deviceEventFilter, filter);
        _.assign(countFilter, filter);
      }
      const promises = [
        fetchDeviceEvents(deviceEventFilter, true, queryKey),
        fetchDeviceEventsCountV2(countFilter, true),
      ];

      const [deviceEvs, countData]: any = await Promise.all(promises);
      if (pageIndex.index > 0) {
        if (
          countData[activeButton] <=
          pageIndex.index *
            STRIP_DEVICE_THUMBNAIL_INFO.stripRow *
            STRIP_DEVICE_THUMBNAIL_INFO.stripPerRow
        ) {
          setPageIndex({ index: 0 });
        }
      }
      const { deviceEvents } = formatDeviceEvents(
        deviceEvs,
        timezone,
        facility,
        startStudyDate,
        activeButtonProp,
      );
      cacheDeviceEvents(deviceEvents);
      const currentDeviceEvents = getDeviceEventData();
      setDeviceEventData({ ...currentDeviceEvents, ...deviceEvents });
      setDeviceEventCount(countData);
    } catch (error) {
      logError('Failed to fetch holter events: ', error);
    } finally {
      setIsLoadingStrip(false);
    }
  };

  /**
   * Refetches the current page of device events after an update or reload signal. Delegates to the page fetch.
   *
   * @returns Nothing.
   */
  const handleReloadDeviceEvents = () => {
    fetchEvents();
  };

  const eventStrips = useMemo(() => {
    const mergeData = _.map(deviceEventData[activeButton], (event) => {
      const eventChange = _.find(changeStripData, (x) => x.id === event.id);
      if (eventChange) {
        return {
          ...event,
          ...eventChange,
        };
      }
      return event;
    });
    return mergeData;
  }, [activeButton, deviceEventData, changeStripData, isReloadStripManagement]);

  // Handle drag select
  /**
   * Records the strip ids covered by the drag selection. Replaces the local selection set, which an effect then resolves into the store's multiple-strip selection.
   *
   * @param newSelected - Set of selection keys produced by the drag-select area.
   * @returns Nothing.
   */
  const setSelectedEvent = (newSelected: any) => {
    setSelected(newSelected);
  };

  useEffect(() => {
    const selectedArr = [...selected];
    const selectedStrips: any[] = [];
    _.forEach(selectedArr, (item) => {
      const id = item.split('-')[1];
      /**
       * Finds the loaded strip whose id matches the one parsed out of a drag-selection key. Scans the merged event strip list, tolerating a list that has not loaded yet.
       *
       * @returns The matching event strip, or undefined when it is not on the current page.
       */
      const selectedEvent = (eventStrips || []).find((strip) => strip.id === id);
      if (selectedEvent) {
        selectedStrips.push(selectedEvent);
      }
    });
    setSelectedMultipleStrip((prev) => {
      if (prev.length === 0 && selectedStrips.length === 0) {
        return prev;
      }
      return selectedStrips;
    });
  }, [selected, eventStrips]);

  /**
   * Selects a clicked device event strip and records its position in the grid. Updates the store only when the clicked id differs from the current selection, storing the row and column derived from the index so arrow-key navigation starts from there.
   *
   * @param event - Device event backing the clicked thumbnail.
   * @param index - Position of the thumbnail within the current page.
   * @returns Nothing.
   */
  const handleClickSelectEvent = useCallback((event: any, index: any) => {
    setSelectedStrip((prev) => {
      if (event?.id !== prev?.id) {
        stripPosition.current = getStripPosition(index, STRIP_DEVICE_THUMBNAIL_INFO.stripPerRow);
        return event;
      }
      return prev;
    });
  }, []);

  useEffect(() => {
    setSelectedStrip((prev) => {
      const findIndex = _.findIndex(eventStrips, { id: prev?.id });
      const findEvent = eventStrips[findIndex];
      if (!findEvent) {
        stripPosition.current = getStripPosition(0, STRIP_DEVICE_THUMBNAIL_INFO.stripPerRow);
        return eventStrips[0];
      }
      stripPosition.current = getStripPosition(findIndex, STRIP_DEVICE_THUMBNAIL_INFO.stripPerRow);
      return findEvent;
    });
  }, [eventStrips]);

  useEffect(() => {
    isActiveQuery.current = true;
    if (isActive) {
      fetchEvents();
    }
    return () => {
      isActiveQuery.current = false;
    };
  }, [activeButton, pageIndex, isActive]);

  useDeepCompareEffect(() => {
    setPageIndex({ index: 0 });
  }, [stripFilter]);

  useEffect(() => {
    if (deviceEventCount) {
      setTotalStrip(deviceEventCount[activeButton]);
    }
  }, [activeButton, deviceEventCount]);

  useEffect(() => {
    /**
     * Moves the strip selection around the thumbnail grid with the arrow keys. Ignores auto-repeat, an in-flight load and keystrokes made while the beat and heart-rate tab is not hovered; left and right step along the row and emit the previous or next page signal at the ends, up and down step between rows, and every move first resets the drag selection.
     *
     * @param event - Keyboard event delivered by the window listener.
     * @returns Nothing.
     */
    const handleKeyDown = (event: any) => {
      if (_.isEmpty(selectionAreaWrapperRef.current) || event?.repeat || isLoadingStrip) {
        return;
      }
      const tabZone = document.getElementById(BEAT_HR_TAB_ID);
      // *:Active key press when hover in tab zone
      if (tabZone && !tabZone.matches(':hover')) {
        return;
      }
      event.preventDefault();
      const { key } = event;
      switch (key) {
        case 'ArrowRight': {
          selectionAreaWrapperRef.current.resetSelection();
          stripPosition.current.y += 1;
          const index = getStripIndex(
            stripPosition.current,
            STRIP_DEVICE_THUMBNAIL_INFO.stripPerRow,
          );
          if (index > eventStrips.length - 1) {
            stripPosition.current.y -= 1;
            emitter.emit(EMITTER_CONSTANTS.STRIP_DEVICE_MANAGEMENT_NEXT);
          } else {
            const event = eventStrips[index];
            if (event) {
              setSelectedStrip(event);
            } else {
              stripPosition.current.y -= 1;
            }
          }
          break;
        }
        case 'ArrowLeft': {
          selectionAreaWrapperRef.current.resetSelection();
          stripPosition.current.y -= 1;
          const index = getStripIndex(
            stripPosition.current,
            STRIP_DEVICE_THUMBNAIL_INFO.stripPerRow,
          );
          if (index < 0) {
            arrowKeyToPrevPage.current = true;
            stripPosition.current.y += 1;
            emitter.emit(EMITTER_CONSTANTS.STRIP_DEVICE_MANAGEMENT_PREV);
          } else {
            const event = eventStrips[index];
            if (event) {
              setSelectedStrip(event);
            } else {
              stripPosition.current.y += 1;
            }
          }
          break;
        }
        case 'ArrowUp': {
          selectionAreaWrapperRef.current.resetSelection();
          stripPosition.current.x -= 1;
          const event =
            eventStrips[
              getStripIndex(stripPosition.current, STRIP_DEVICE_THUMBNAIL_INFO.stripPerRow)
            ];
          if (event) {
            setSelectedStrip(event);
          } else {
            stripPosition.current.x += 1;
          }
          break;
        }
        case 'ArrowDown': {
          selectionAreaWrapperRef.current.resetSelection();
          stripPosition.current.x += 1;
          const event =
            eventStrips[
              getStripIndex(stripPosition.current, STRIP_DEVICE_THUMBNAIL_INFO.stripPerRow)
            ];
          if (event) {
            setSelectedStrip(event);
          } else {
            stripPosition.current.x -= 1;
          }
          break;
        }
        default:
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [eventStrips, isLoadingStrip]);

  useUpdateEffect(() => {
    const { x: row, y: column } = stripPosition.current;
    const currentStripIndexInList = column + row * STRIP_DEVICE_THUMBNAIL_INFO.prevStripPerRow;
    const currentEventIndex =
      currentStripIndexInList + pageIndex.index * STRIP_DEVICE_THUMBNAIL_INFO.prevStripDisplayLimit;
    const newPage = Math.floor(currentEventIndex / STRIP_DEVICE_THUMBNAIL_INFO.stripDisplayLimit);
    setPageIndex({ index: newPage < 0 ? 0 : newPage });
  }, [sizeThumbnailConfig]);

  useEmitter(EMITTER_CONSTANTS.UPDATE_EVENT_SUCCESS, handleReloadDeviceEvents, [
    activeButton,
    stripFilter,
    pageIndex,
  ]);
  useEmitter(EMITTER_CONSTANTS.RELOAD_EVENT_STRIP, handleReloadDeviceEvents, [
    activeButton,
    stripFilter,
    pageIndex,
  ]);

  return {
    selectionAreaWrapperRef,
    setSelectedEvent,
    selectedStrip,
    eventStrips,
    isLoadingStrip,
    selectedMultipleStrip,
    sizeThumbnailConfig,
    handleClickSelectEvent,
  };
};

export default useDeviceStripEvents;
